import { getSession, setSession } from '@/lib/session'
import { fetchAuthzContext } from '@/api/authorization'
import { resolvePostLoginRoute } from '@/lib/postLoginRoute'
import { fetchUserSession } from './api'
import type { AuthSession } from './types'
import type { UserSessionResponse } from './types'
import { UserTypes } from '@/types/authorization'

export function tokenExpiryFromSeconds(expirySeconds: number): string {
  const d = new Date()
  d.setSeconds(d.getSeconds() + Number(expirySeconds) - 600)
  return d.toISOString()
}

function normalizeNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value !== 'string' || value.trim() === '') return undefined
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

function normalizeUserTypeId(userSession: UserSessionResponse): UserTypes | undefined {
  const parsed = normalizeNumber(userSession.currentUserTypeId ?? userSession.CurrentUserTypeId)
  return Object.values(UserTypes).includes(parsed as UserTypes) ? (parsed as UserTypes) : undefined
}

export async function buildAuthSession(
  email: string,
  idToken: string,
  refreshToken: string,
  expirySeconds: number
): Promise<AuthSession> {
  const tokenExpiry = tokenExpiryFromSeconds(expirySeconds)
  setSession({
    email,
    idToken,
    refreshToken,
    tokenExpiry,
    currentClientId: '',
    defaultClientId: '',
  })
  const userSession = await fetchUserSession(email)
  return {
    email,
    idToken,
    refreshToken,
    tokenExpiry,
    currentClientId: userSession.currentClientId ?? '',
    defaultClientId: userSession.defaultClientId ?? userSession.DefaultClientId ?? userSession.currentClientId ?? '',
    currentUserId: userSession.currentUserId,
    currentUsername: userSession.currentUsername,
    currentUserTypeId: normalizeUserTypeId(userSession),
    currentClientTypeId: userSession.currentClientTypeId,
    defaultClientTypeId:
      userSession.defaultClientTypeId ?? userSession.DefaultClientTypeId ?? userSession.currentClientTypeId,
    currentClientName: userSession.currentClientName,
    defaultClientName: userSession.defaultClientName ?? userSession.DefaultClientName ?? userSession.currentClientName,
    currentClientShowProjectStatus: userSession.currentClientShowProjectStatus ?? userSession.ShowProjectStatus,
    currentClientShowAdvisoryMapping: userSession.currentClientShowAdvisoryMapping ?? userSession.ShowAdvisoryMapping,
    defaultPageSize: userSession.defaultPageSize,
    platformType: userSession.platformType ?? userSession.PlatformType,
    advisorId: userSession.advisorId ?? userSession.AdvisorId,
  }
}

export function mergeSessionWithUser(
  email: string,
  idToken: string,
  refreshToken: string,
  tokenExpiry: string,
  userSession: UserSessionResponse
): AuthSession {
  return {
    email,
    idToken,
    refreshToken,
    tokenExpiry,
    currentClientId: userSession.currentClientId ?? '',
    defaultClientId: userSession.defaultClientId ?? userSession.DefaultClientId ?? userSession.currentClientId ?? '',
    currentUserId: userSession.currentUserId,
    currentUsername: userSession.currentUsername,
    currentUserTypeId: normalizeUserTypeId(userSession),
    currentClientTypeId: userSession.currentClientTypeId,
    defaultClientTypeId:
      userSession.defaultClientTypeId ?? userSession.DefaultClientTypeId ?? userSession.currentClientTypeId,
    currentClientName: userSession.currentClientName,
    defaultClientName: userSession.defaultClientName ?? userSession.DefaultClientName ?? userSession.currentClientName,
    currentClientShowProjectStatus: userSession.currentClientShowProjectStatus ?? userSession.ShowProjectStatus,
    currentClientShowAdvisoryMapping: userSession.currentClientShowAdvisoryMapping ?? userSession.ShowAdvisoryMapping,
    defaultPageSize: userSession.defaultPageSize,
    platformType: userSession.platformType ?? userSession.PlatformType,
    advisorId: userSession.advisorId ?? userSession.AdvisorId,
  }
}

export async function refreshSessionFromUser(email: string): Promise<AuthSession> {
  const existing = getSession()
  if (!existing?.idToken || !existing.refreshToken || !existing.tokenExpiry) {
    throw new Error('No existing session')
  }
  const userSession = await fetchUserSession(email)
  return mergeSessionWithUser(
    email,
    existing.idToken,
    existing.refreshToken,
    existing.tokenExpiry,
    userSession
  )
}

export async function resolveLatestPostLoginRoute(session: AuthSession): Promise<string> {
  const fallbackRoute = resolvePostLoginRoute(session)
  const clientId = session.currentClientId || session.defaultClientId || ''

  if (!clientId) return fallbackRoute

  try {
    const context = await fetchAuthzContext(clientId)
    const defaultRoute = typeof context?.defaultRoute === 'string' ? context.defaultRoute.trim() : ''
    if (defaultRoute.startsWith('/')) return defaultRoute
  } catch {
    // Fall back to the session-derived route when authz context is unavailable.
  }

  return fallbackRoute
}
